import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Slider from '@radix-ui/react-slider';
import classNames from 'classnames';

import 'tailwindcss/tailwind.css';

interface RangeSliderProps {
  values: number[];
  onValueChange: (values: number[]) => void;
  min: number;
  max: number;
  step: number;
  labelFormatter?: (label: number) => string;
  rootClassName?: string;
  trackClassName?: string;
  rangeClassName?: string;
  thumbClassName?: string;
  labelClassName?: string;
  showStepLabels?: boolean;
  ariaLabelledBy?: string;
  ariaLabel?: string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  values,
  onValueChange,
  min,
  max,
  step,
  labelFormatter,
  rootClassName = 'relative flex h-5 w-full touch-none select-none items-center',
  trackClassName = 'bg-gray relative grow rounded-full h-[0.5rem]',
  rangeClassName = 'absolute bg-primary rounded-full h-full',
  thumbClassName = 'block h-6 w-6 rounded-full bg-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 relative',
  labelClassName = 'flex flex-wrap w-full justify-between pt-2',
  showStepLabels = false,
  ariaLabelledBy,
  ariaLabel,
}) => {
  const [internalValues, setInternalValues] = useState(values);
  const sliderRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    setInternalValues(values);
  }, [values]);

  const getThumbPosition = (value: number) => {
    return ((value - min) / (max - min)) * 100;
  };

  const stepValues = useMemo(() => {
    const steps: number[] = [];
    for (let i = min; i <= max; i += step) {
      steps.push(i);
    }
    return steps;
  }, [min, max, step]);

  return (
    <div
      id="slider-id-container"
      className={classNames(
        'relative flex w-full flex-col items-start',
        showStepLabels ? 'h-20' : 'h-10'
      )}
    >
      <Slider.Root
        minStepsBetweenThumbs={step}
        ref={sliderRef}
        className={rootClassName}
        value={internalValues}
        onValueChange={setInternalValues}
        onValueCommit={onValueChange}
        min={min}
        max={max}
        step={step}
      >
        <Slider.Track className={trackClassName}>
          <Slider.Range className={rangeClassName} />
        </Slider.Track>
        {internalValues.map((value, index) => (
          <Slider.Thumb
            key={index}
            className={thumbClassName}
            aria-labelledby={ariaLabelledBy}
            aria-label={ariaLabel}
            aria-valuetext={labelFormatter ? labelFormatter(value) : value.toString()}
          />
        ))}
      </Slider.Root>
      {showStepLabels ? (
        <div className="relative h-8 w-full">
          {stepValues.map((value: number, index: number, array: number[]) => (
            <span
              key={value}
              className={classNames(
                'pointer-events-none absolute top-4 w-full max-w-20 text-wrap text-sm',
                {
                  'text-left': index === 0,
                  '-translate-x-1/2 text-center': index > 0 && index < array.length - 1,
                  '-translate-x-full text-right': index === array.length - 1,
                }
              )}
              style={{ left: `${getThumbPosition(value)}%` }}
            >
              {labelFormatter ? labelFormatter(value) : value}
            </span>
          ))}
        </div>
      ) : (
        <div className={labelClassName}>
          {internalValues.map((value, index) => (
            <div key={index}>{labelFormatter ? labelFormatter(value) : value}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RangeSlider;
